import type { InlineCardAttributes, InlineCardItemAttributeSpec, InlineCardOptions } from "./types";
import { isNodeSelection, mergeAttributes, Node, VueNodeViewRenderer } from "@tiptap/vue-3";
import { setHttpsProtocolIfNeeded } from "@/common/lib/string";
import { preventNodeMarks } from "../../preventNodeMarks";
import { enterKeyHandler } from "../enterKeyHandler";
import InlineCard from "./InlineCard.vue";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    inlineCard: {
      /**
       * Set inline card
       */
      setInlineCard: (attributes: InlineCardAttributes) => ReturnType;

      /**
       * Unlinks inline card
       */
      unlinkInlineCard: (text?: string) => ReturnType;
    };
  }
}

export default Node.create<InlineCardOptions>({
  name: "inlineCard",
  marks: "",
  group: "inline",
  inline: true,
  selectable: true,
  atom: true,

  addAttributes() {
    return {
      url: {
        default: null,
        parseHTML: element => element.getAttribute("href"),
        renderHTML: attrs => ({ href: (attrs as InlineCardAttributes).url || "" }),
      },
      text: {
        default: null,
        rendered: false,
        parseHTML: element => {
          const text = element.textContent;
          const href = element.getAttribute("href");
          // when link text matches the href attribute keep text default value (null)
          // this is required to properly handle copy-paste of Canvas links that didn't have custom display text.
          // by keeping it default link display text will be re-fetched from the canvas itself
          if (text === href) {
            return null;
          }
          return text;
        },
      },
    } satisfies InlineCardItemAttributeSpec;
  },

  parseHTML() {
    return [{
      tag: `a[data-type="${this.name}"]`,
      // set priority higher than default 'link' mark
      priority: 51,
    }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const attrs = node.attrs as InlineCardAttributes;
    return [
      "a",
      mergeAttributes({
        "data-type": this.name,
        href: attrs.url,
      }, HTMLAttributes),
      attrs.text || attrs.url,
    ];
  },

  addNodeView() {
    return VueNodeViewRenderer(InlineCard);
  },

  renderText({ node }) {
    const attrs = node.attrs as InlineCardAttributes;
    return attrs.text || attrs.url;
  },

  addProseMirrorPlugins() {
    return [
      preventNodeMarks({
        type: this.type,
      }),

      enterKeyHandler({
        type: this.type,
        getUrl(attributes) {
          const attrs = attributes as unknown as InlineCardAttributes;
          return attrs.url;
        },
      }),
    ];
  },

  addCommands() {
    return {
      setInlineCard: attributes => ({ commands }) => {
        // normalize url to include protocol if needed
        attributes.url = setHttpsProtocolIfNeeded(attributes.url);
        return commands.insertContent({
          type: this.name,
          attrs: attributes,
        });
      },

      unlinkInlineCard: text => ({ chain, state }) => {
        const { selection } = state;

        if (!isNodeSelection(selection) || selection.node.type !== this.type) {
          return false;
        }

        const node = selection.node;
        const attrs = node.attrs as InlineCardAttributes | undefined;
        if (!attrs) return false;

        const textToSet = text || attrs.url;
        const textNode = state.schema.text(textToSet);

        return chain()
          .command(({ tr }) => {
            tr.replaceSelectionWith(textNode);
            return true;
          })
          .setMeta("preventAutolink", true)
          .run();
      },
    };
  },
});
